import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import type { ColumnsType } from 'antd/es/table';
import {
  Button, Typography, Input,
  Form, notification, DatePicker
} from 'antd';
import {
  TableOutlined, LineChartOutlined, LoadingOutlined,
  ReloadOutlined
 } from '@ant-design/icons';
//@ts-ignore
import { BlockSelect, RanchSelect, EntitySelect,
   nodeStatusUnits, nodeStatusKeys, excludedNodeKeys,
   prepareGraphData, nodeOperationsTableFieldNames,
} from 'farmx-web-ui';
import { actions, hooks, selectors } from 'farmx-redux-core';
import { useDispatch, useSelector } from 'react-redux';
import { FaChevronLeft } from 'react-icons/fa';
import EditableTable from './EditableTable';
import { isMobile } from '../../../utils/detectDevice';
import './ruleSettingsPage.less';
import { 
  FORM_RULES, ALL_SENSORS_LIST, nodeMetaDataLabels
} from '../../../constants';
import Page404 from 'components/app/Page404';
import { useRenderReusableControls } from 'helper/reusableControls';
import { Block, Ranch } from 'types/farm';
import {
   getNodeHealthData, getNodeStatusData, getTableField,
   useGetMaxWidth, getNodeMetaData, getNodeAssociatedSensors,
   getNodeMetaDataFiled, sortByDateWithKey, rebootNodeByIdentifier,
   renderFieldValue
} from 'helper/common';
import { useUserTimeFormat } from 'helper/commonHooks';
import NodeHealthGraph from './NodeHealthGraph';
import ReusableModal from 'components/mobile/components/ReusableModal';
import LoraNodeGraph from './LoraNodeGraph';

const now = moment();
const defaultDateRange = [now.startOf('day').clone().subtract(3, 'days'), now.endOf('day')];
const modalWidth = 500;

const variable = 'health';
const paramStr = '?sensor_type=cavalier';
const loraVariables = 'temperature,battery_voltage,sequence_number,battery_percentage,cap_charge,gateway_rssi,gateway_snr,spreading_factor,frequency,bandwidth,interval';
const nonLoraNodeSubtypes = ['field_node', 'dragino_cellular', 'milesight_cellular'];
const loraNodeSubtypes = ['lora_sensor_node', 'lora_dragino'];
const sensorSubtypeOptions = [
  { value: 'all', label: 'All'},
  { value: 'field_node', label: 'Field Node'},
  { value: 'lora_sensor_node', label: 'Lora Sensor Node'},
  { value: 'lora_dragino', label: 'Dragino Lora'},
  { value: 'dragino_cellular', label: 'Dragino Cellular'},
  { value: 'milesight_cellular', label: 'Milesight Cellular'},
]
const defaultSensorSubtype = 'all';
const { Search } = Input;
const { RangePicker } = DatePicker;

// Add/Remove fields to display on the table
const allowedHeaders = ["index", "name", "identifier", "sub_type", "rssi", "latest_date",
  "fw_version", "git_info", "battery_voltage", "external_voltage", "core_temp"];

const allowedHeadersForLora = [
    "index", "name", "identifier", "sub_type", "latest_date", "bandwidth", "frequency", "cap_charge",
    "alarm_count", "gateway_snr", "provisioned", "temperature", "gateway_rssi", "battery_voltage",
    "data_count_24hr", "sequence_number", "spreading_factor", "battery_percentage", "data_count_expected",
]

const allowedNodeHealthHeaders = ["index", "date", "rssi", "provider", "networkType", "rebootTime",
  "rebootReason", "solarVoltage", "batteryVoltage", "chargingCurrent", "chargingStatus",
  "externalVoltage", "upTime", "coreTemp", "heapMemoryFree", "fwAPartFree",  "fwBPartFree",
  "configAPartFree", "sensorDataPartFree", "systemLogsPartFree", "scratchAreaPartFree"];

const { loadNewRanchList, loadNewBlockList, loadSensorsForEntity } = actions;

const {
  selectAllNewRanches, selectAllNewBlocks, selectAllNewEntities,
  selectLoginUserInfo
} = selectors;

const {
  useBlockNames, useRanchNames, useEntityNames,
  useSensorsForEntity
} = hooks;

type NewBlock = Block & {
  ranch: Ranch | null | undefined
}

const NodeOperations: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [showControl, setShowControl] = useState(false);
  const [showGraph, setShowGraph] = useState(false);
  const [showLoraGraph, setShowLoraGraph] = useState(false);
  const [configList, setConfigList] = useState<any>([]);
  const [allNodes, setAllNodes] = useState<any>([]);
  const [healthData, setHealthData] = useState<any>([]);
  const [selected, setSelected] = useState<any>(undefined);
  const [graphData, setGraphData] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<any>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [localRanch, setLocalRanch] = useState(undefined);
  const [localBlock, setLocalBlock] = useState(undefined);
  const [localEntity, setLocalEntity] = useState(undefined);
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [loadingMap, setLoadingMap] = useState<Record<string, Record<string, boolean>>>({});
  const [metaDataLoading, setMetaDataLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [metaData, setMetaData] = useState<any>(undefined);
  const [pageSize] = useState(10); // Number of sensor call need per page (Minimum: 10)
  const [aSLoading, setASLoading] = useState(false);
  const [showRebootModal, setShowRebootModal] = useState(false);
  const [selectedNode, setSelectedNode] = useState<any>(undefined);
  const [selectedSensorSubtype, setSelectedSensorSubtype] = useState<string>(defaultSensorSubtype);

  const { renderButton, renderSelect } = useRenderReusableControls(t);
  const rules = FORM_RULES(t);
  const allRanches: any[] = useSelector(selectAllNewRanches);
  const allBlocks: any[] = useSelector(selectAllNewBlocks);
  const allEntities: any[] = useSelector(selectAllNewEntities);
  const ranches = allRanches.filter((ranch) => (ranch?.entity)?.id === localEntity);
  const blocks = allBlocks.filter((block: NewBlock) => (block?.ranch)?.id === localRanch);
  const maxWidth = useGetMaxWidth();
  const userTimeFormat = useUserTimeFormat();
  const localRanchIds = allRanches?.map((item: any) => item?.id);
  const localBlocksIds = allBlocks?.map((item: any) => item?.id);
  const localEntityIds = allEntities?.map((item: any) => item?.id);
  const blockNames = useBlockNames(localBlocksIds) || {};
  const ranchNames = useRanchNames(localRanchIds) || {};
  const entityNames = useEntityNames(localEntityIds) || {};
  const sensorsForEntity = useSensorsForEntity(localEntity, "cavalier");
  const userInfo: any = useSelector(selectLoginUserInfo)?.payload;
  const isAdmin = (userInfo && userInfo.admin);

  /* eslint-disable no-template-curly-in-string */
  const validateMessages = {
    required: t('${label} is required!'),
  };
  
  moment.locale('en');

  const tableFieldNames = nodeOperationsTableFieldNames;
  const units = nodeStatusUnits; 

  // To decide the column width and render options
  const tableFieldExtra = getTableField(sensorsForEntity, t);
  const metaDataTableFieldExtra = getNodeMetaDataFiled(t);

  const sortedConfigData: any[] = sortByDateWithKey(configList, 'latest_date', true);
  const sortedHealthData: any[] = sortByDateWithKey(healthData, 'date', true);

  useEffect(() => {
    if (!localEntity) return;
    dispatch(loadNewRanchList(localEntity));
    dispatch(loadNewBlockList(localEntity));
    dispatch(loadSensorsForEntity(localEntity));
  }, [dispatch, localEntity]);
  
  // Function to call associated sensors API and update config list
  const handlePageChange = async (page, pageSize, filteredNodes: any = []) => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = page * pageSize;
    const dataToUpdate = filteredNodes;
    const chunk = dataToUpdate.slice(startIndex, endIndex);
    setASLoading(true);

    getNodeAssociatedSensors(chunk, (sensors) => {
      const associatedSensors = sensors
        ?.filter((item: any) => item?.nodeIdentifier)
        ?.reduce(
          (acc, obj) => ({
            ...acc,
            [obj?.nodeIdentifier]: obj?.sensors,
          }),
          {}
        );

      const updatedFilteredData = dataToUpdate.map((item: any) => {
        if (!item?.associatedSensors) {
          return {
            ...item,
            associatedSensors: associatedSensors?.[item?.identifier] || [],
            sensorCount: associatedSensors?.[item?.identifier]?.length || 0,
          };
        }
        return item;
      });

      setConfigList(updatedFilteredData);
      setAllNodes(updatedFilteredData);
      setASLoading(false);
    });
  };

  if (isMobile || !isAdmin) return <Page404 />;

  const getStickyColumns = (type) => {
    const stickyColumns = [
      {
        title: t('Graph'),
        key: 'graph',
        fixed: 'right',
        render: (_, record) => (
          <Button
            type="primary"
            className="icon-action-btn"
            title={t('Graph')}
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(record.key, record, true)}
            }
            icon={<LineChartOutlined />}
            loading={loadingMap?.[record.key]?.graph || false}
            disabled={healthLoading && !loadingMap?.[record.key]?.graph}
          />
        ),
        width: 50
      },
      {
        title: t('Table'),
        key: 'table',
        fixed: 'right',
        render: (_, record) => (
          <Button
            type="primary"
            className="icon-action-btn"
            title={t('Table')}
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(record.key, record, false)
            }}
            icon={<TableOutlined />}
            loading={loadingMap?.[record.key]?.table || false}
            disabled={healthLoading && !loadingMap?.[record.key]?.table}
          />
        ),
        width: 50
      },
      {
          title: t('Sensor Count'),
          key: 'sensorCount',
          fixed: 'right',
          render: (_, record: any) => (
            <Button
              type="ghost"
              onClick={(e) =>  {
                e.stopPropagation();
                handleSensorCountClick(record);
              }}
              disabled={!record?.sensorCount}
            >
              {record?.sensorCount || 0}
            </Button>
          ),
          width: 70
      },
      {
        title: t('Reboot'),
        key: 'reboot',
        fixed: 'right',
        render: (_, record) => (
          <Button
            type="primary"
            className="icon-action-btn"
            title={t('Reboot')}
            danger
            onClick={(e) => {
              e.stopPropagation();
              handleNodeReboot(record);
            }}
            icon={<ReloadOutlined />}
            loading={loadingMap?.[record.key]?.reboot || false}
            disabled={!record?.online 
              || healthLoading && !loadingMap?.[record.key]?.table
              || healthLoading && !loadingMap?.[record.key]?.graph
            }
          />
        ),
        width: 50
      }];

    return stickyColumns
  };

  function handleNodeReboot(record: any) {
    setSelectedNode(record);
    setShowRebootModal(true);
  }

  function handleSensorCountClick(record: any) {
    sessionStorage.setItem("associatedSensorsData", JSON.stringify(record));

    // Open new tab
    window.open("/admin-operations/associated-sensors", "_blank");
  }

  function getDynamicColumn(type) {
    // To process the columns dynamically
    let headers = [...allowedHeaders];
    if (!type || type === defaultSensorSubtype) headers = [...new Set([...allowedHeaders, ...allowedHeadersForLora])];
    else if (type === 'lora_sensor_node' || type === 'lora_dragino') headers = [...allowedHeadersForLora]

    const tableColumns: ColumnsType = (!selected ? headers : allowedNodeHealthHeaders)
      ?.map((field) => ({
        title: `${t(tableFieldNames?.[field])} ${(units?.[field]
          || units?.[nodeStatusKeys?.[field]]) ? `(${(units?.[field]?.shortName || units?.[field])
          || (units?.[nodeStatusKeys?.[field]]?.shortName || units?.[nodeStatusKeys?.[field]])})` : ''}`,
        dataIndex: field,
        key: field,
        ...(tableFieldExtra?.[field] || { width: 100, render: (_, record) => renderFieldValue(record?.[field]) }),
        ...(units?.[field] ? { width: (tableFieldExtra?.[field]?.width || 150) } : { width: 100 }),
        ...(units?.[nodeStatusKeys?.[field]] ? { width: (tableFieldExtra?.[nodeStatusKeys?.[field]]?.width || 150) } : { width: 100 }),
      }));  
    const columns = [...tableColumns, ...(!selected ? getStickyColumns(type) : [])]

    return columns;
  }

  const onGoBack = () => {
    setLoading(false);
    setHealthLoading(false);
    setDateRange(defaultDateRange);
    if (showControl) setShowControl(false);
    if (showGraph) setShowGraph(false);
    if (showLoraGraph) setShowLoraGraph(false);
    if (selected) {
      setSelected(undefined);
      setHealthData([]);
    }
  };

  function onSearch(event: any) {
    const searchStr = event?.target?.value?.toLowerCase() || '';
    if (!selected) {
      const filteredList = allNodes?.filter((item: any) => {
        const idMatch = item?.identifier?.toLowerCase()?.includes(searchStr);
        const nameMatch = item?.name?.toLowerCase()?.includes(searchStr);
        return idMatch || nameMatch;
      }) || [];
      setConfigList(filteredList);
    }
    if (selected) {
      const filteredList = selected?.data?.filter((item: any) => {
        const dateMatch = moment(item?.date).format("YYYY-MM-DD HH:mm:ss")?.includes(searchStr);
        return dateMatch;
      }) || [];
      setHealthData(filteredList);
    }
  }

  function callNodeHealthAPIAndGetData(type, id, variables, isGraph?: any, rowKey?: any) {
    if (dateRange?.length) {
      if(rowKey) {
        setLoadingMap((prev) => ({
          ...prev,
          [rowKey]: { ...prev[rowKey], ...(isGraph? { graph: true } : { table: true }) }
        }));
      }
      setHealthLoading(true);
      getNodeHealthData(type, id, variables, dateRange[0], dateRange[1], (response, success) => {
        if (success) {
          const { sensor_details, graph_data } = response || {};
          const selectedNode = { sensor_details, data: graph_data?.[variable]?.data || []}
          const graphData = prepareGraphData(selectedNode?.data);
          setGraphData(graphData);
          setSelected(selectedNode);
          setHealthData(selectedNode?.data);
          if (!isGraph) {
            setShowControl(true)
          }
          if (isGraph) {
            setShowGraph(true)
          }
          setHealthLoading(false);
        } else {
          notification.error({
            message: t('something went wrong'),
            description: response?.message,
            maxCount: 1
          })
          setHealthLoading(false);
        }
        if(rowKey) {
          setLoadingMap((prev) => ({
            ...prev,
            [rowKey]: { ...prev[rowKey], ...(isGraph? { graph: false } : { table: false }) }
          }));
        }
      })
    } else {
      notification.error({
        message: t('Date range is required.'),
        description: t('Please select the date range'),
        maxCount: 1
      })
    }
  }

  function callNodeGraphData(type, id, variables, isGraph?: any, rowKey?: any) {
    if (dateRange?.length) {
      if(rowKey) {
        setLoadingMap((prev) => ({
          ...prev,
          [rowKey]: { ...prev[rowKey], ...(isGraph? { graph: true } : { table: true }) }
        }));
      }
      setHealthLoading(true);
      getNodeHealthData(type, id, variables, dateRange[0], dateRange[1], (response, success) => {
        if (success) {
          const { sensor_details, graph_data } = response || {};
          const graphData = { sensor_details: {...sensor_details, type: 'lora'}, ...graph_data || {}}
          setGraphData(graphData);
          setSelected(graphData);
          if (!isGraph) {
            setShowControl(true)
          }
          if (isGraph) {
            setShowLoraGraph(true)
          }
          setHealthLoading(false);
        } else {
          notification.error({
            message: t('something went wrong'),
            description: response?.message,
            maxCount: 1
          })
          setHealthLoading(false);
        }
        if(rowKey) {
          setLoadingMap((prev) => ({
            ...prev,
            [rowKey]: { ...prev[rowKey], ...(isGraph? { graph: false } : { table: false }) }
          }));
        }
      })
    } else {
      notification.error({
        message: t('Date range is required.'),
        description: t('Please select the date range'),
        maxCount: 1
      })
    }
  }

  function handleRowClick(recordKey: any, record: any, isGraph?: any) {
    const [filteredNode] = allNodes.filter((node: any) => (node.identifier === record.identifier));
    const { type, id } = filteredNode || {};
    if (nonLoraNodeSubtypes.includes(record?.sub_type)) {
      callNodeHealthAPIAndGetData(type, id, [variable], isGraph, recordKey);
    }
    if (loraNodeSubtypes.includes(record?.sub_type)) {
      callNodeGraphData(type, id, [loraVariables], isGraph, recordKey) 
    }
  }

  function onDateRangeLoad() {
    const { sensor_type: type, sensor_uid: id, type: nodeType } = selected?.sensor_details || {};
    if (!nodeType) {
      callNodeHealthAPIAndGetData(type, id, [variable], showGraph);
    }
    if (nodeType === 'lora') {
      callNodeGraphData(type, id, [loraVariables], showLoraGraph) 
    }
  }

  // This will be updated with API call logic
  function onFinish(formObj: any, isRefresh?: any) {
    setSelectedSensorSubtype(defaultSensorSubtype);
    if (!isRefresh) setLoading(true);
    const { ranch, block } = formObj;
    let type = 'entity';
    let id = localEntity;
    if (ranch && !block) {
      type = 'ranch';
      id = ranch;
    }
    if (block) {
      type = 'block';
      id = block;
    }
  
    if (isRefresh) setRefreshLoading(true);
    getNodeStatusData(type, id, paramStr, (response, success) => {
      if (success) {
        const filteredNodes = response.filter((item: any) => (item.sub_type));
        setConfigList(filteredNodes);
        setAllNodes(filteredNodes)
        handlePageChange(1, pageSize, filteredNodes)
      } else {
        notification.error({
          message: t('something went wrong'),
          description: response?.message,
          maxCount: 1
        })
      }
      if (!isRefresh) setLoading(false);
      if (isRefresh) {
        setRefreshLoading(false);
        setLastUpdated(moment().format('YYYY-MM-DD HH:mm:ss'));
      }
    })
  }

  const onNodeFilter = (value: any) => {
    setSelectedSensorSubtype(value);
    const filteredNodes = (value && value !== defaultSensorSubtype)
      ? allNodes.filter((item: any) => (item.sub_type === value))
      : allNodes;
    setConfigList(filteredNodes);
  }

  const title = t('Node Health Status');

  function renderFilterForm() {
    return (
      <Form
        className="node-operations-filter-form"
        form={form}
        layout={isMobile ? "vertical" : "inline"}
        name="Filters"
        onFinish={(d) => onFinish(d)}
        validateMessages={validateMessages}
        initialValues={{ entity: localEntity }}
      >
        <Form.Item className="font-bold" name="entity" label={t('Entity')} rules={rules?.requiredField}>
          <EntitySelect value={[localEntity]} onChange={(d: any) => {
            form?.setFieldsValue({ entity: d });
            form.setFieldsValue({ ranch: undefined });
            form.setFieldsValue({ block: undefined });
            setLocalEntity(d);
            setLocalRanch(undefined);
            setLocalBlock(undefined);
            setSelectedSensorSubtype(defaultSensorSubtype);
            setConfigList(allNodes);
          }}/>
        </Form.Item>
        {localEntity && <Form.Item className="font-bold" name="ranch" label={t('Ranch')}>
          <RanchSelect value={[localRanch]} items={ranches} onChange={(d: any) => {
            form?.setFieldsValue({ ranch: d });
            form.setFieldsValue({ block: undefined });
            setLocalRanch(d);
            setLocalBlock(undefined);
          }}/>
        </Form.Item>}
        {localRanch && <Form.Item className="font-bold" name="block" label={t('Block')}>
          <BlockSelect value={[localBlock]} items={blocks} onChange={(d: any) => {
            form?.setFieldsValue({ block: d });
            setLocalBlock(d);
          }}/>
        </Form.Item>}
        <Form.Item className="font-bold">
          {renderButton('Load', 'primary', 'submit', undefined, refreshLoading, undefined, loading)}
        </Form.Item>
      </Form>)
  }

  function renderSearch(placeholderStr?: any) {
    return (
      <div className="btn-align-end margin-bottom-0">
        <div className="margin-left-20">
          <span className="font-bold ">{`${t('Node Filter')}:`}</span>
          {renderSelect(sensorSubtypeOptions, 'Node type', selectedSensorSubtype, onNodeFilter,
            undefined, undefined, 'margin-left-10 node-filter-box'
          )}
        </div>
        <Search
          className="search-box margin-left-20"
          placeholder={t(placeholderStr || "Identifier, Name")}
          onChange={onSearch}
          allowClear
        />
      </div>
    )
  }

  function renderRanchBlockLabel(entity?: any, ranch?: any, block?: any) {
    return (<>
      <div>
        <span className="font-bold">{`${t('Entity')}: `}</span>
        <span>
          {entityNames?.[entity || '']}
        </span>
      </div>
      {ranch && <div className="margin-left-20">
        <span className="font-bold">{`${t('Ranch')}: `}</span>
        <span>
          {ranchNames?.[ranch || '']}
        </span>
      </div>}
      {block && <div className="margin-left-20">
        <span className="font-bold">{`${t('Block')}: `}</span>
        <span>
          {blockNames?.[block || '']}
        </span>
      </div>}
    </>)
  }

  function renderHeaderControls(hideSearch?: any) {
    return <>
      <div className="flex-space-between">
        <div className={`${(isMobile && title?.length > 50)
          ? 'page-header-length' : 'page-header'}`}
        >
          <div className="page-header-main back-header">
            <Button
              id="goBack"
              className="back-btn"
              shape="circle"
              size="large"
              icon={<FaChevronLeft size={20} />}
              onClick={() => onGoBack()}
            />
            <div className="rule-setting-form-title margin-left-0">
              <Typography.Title level={1}>{title}</Typography.Title>
            </div>
          </div>
        </div>
        <div className="btn-align-end margin-bottom-0">
          <RangePicker
            showTime
            placeholder={[t('Start Date'), t('End Date')]}
            onChange={(d: any) => setDateRange(d)}
            value={dateRange}
            format={userTimeFormat.unitsTime
              ? 'YYYY-MM-DD hh:mm:ss a' : 'YYYY-MM-DD HH:mm:ss'}
            defaultValue={dateRange}
            use12Hours={userTimeFormat.unitsTime}
            onOk={null}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          />
          {renderButton('Load', 'primary', 'submit', onDateRangeLoad,
            false, undefined, healthLoading, 'margin-left-10 btn-width-100')}
        </div>
      </div>
      <div className="flex-space-between">
        {renderRanchBlockLabel(localEntity, localRanch, localBlock)}
        <div>
          <span className="font-bold">{`${t('Node')}: `}</span>
          <span>
            {selected?.sensor_details?.sensor_name?.length
              ? selected?.sensor_details?.sensor_name : t('No Name')}
          </span>
        </div>
        <div>
          <span className="font-bold">{`${t('Identifier')}: `}</span>
          <span>{selected?.sensor_details?.sensor_id}</span>
        </div>
        <div>
          <span className="font-bold">{`${t('Type')}: `}</span>
          <span>{selected?.sensor_details?.sensor_type}</span>
        </div>
        {!hideSearch && renderSearch("Date Time")}
      </div>
    </>
  }

  function renderNodeHealthInfo() {
    return <>
      {showControl && !showLoraGraph && (
        <>
          {renderHeaderControls()}
          {healthData?.length
            ? (
              <EditableTable
                data={sortedHealthData}
                columns={getDynamicColumn(selectedSensorSubtype)}
                showIsActive={false}
                showAction={false}
                scroll={{ x: getDynamicColumn(selectedSensorSubtype)?.length * 250}}
                loading={healthLoading}
                rowClassName={(_, index) =>
                  index % 2 === 0 ? "" : "table-row-dark"
                }
                isDataSorted
              />
            ) : (
              <div className="no-data-card text-center">
                <Typography.Text>{t('No Data')}</Typography.Text>
              </div>
            )}
        </>
      )}
    </>
  }

  function renderNodeHealthGraph() {
    const filteredItems = allowedNodeHealthHeaders
      ?.filter((key: string) => !excludedNodeKeys.includes(key));
    return <>
      {showGraph && !showLoraGraph && (
        <>
          {renderHeaderControls(true)}
          <NodeHealthGraph items={filteredItems}
            graphData={graphData}
            startDate={dateRange?.[0]}
            endDate={dateRange?.[1]}
          />
        </>
      )}
    </>
  }

  function renderLoraNodeGraph() {
    const filteredItems = loraVariables?.split(',');
    return <>
      {showLoraGraph && (
        <>
          {renderHeaderControls(true)}
          <LoraNodeGraph items={filteredItems}
            graphData={graphData}
            startDate={dateRange?.[0]}
            endDate={dateRange?.[1]}
          />
        </>
      )}
    </>
  }
  
  function onRowClick(record: any) {
    const [filteredNode] = allNodes.filter((node: any) => (node.identifier === record.identifier));
    const {
      type, identifier, entity: entityId,
      ranch: ranchId, block: blockId
    } = filteredNode || {};
    const sensor = sensorsForEntity[`${type}/${identifier}`];
    const { entity, ranch, block } = sensor || {};
    setShowModal(true);
    setMetaDataLoading(true);
    getNodeMetaData(identifier, (response, success) => {
      if (success) {
        const processedResponse = Object.keys(response)?.map((key) => {
          let processedValue = response[key] || '-';
          if (key === 'manufacture_datetime' && response[key]) {
            processedValue = moment(response[key]).format('YYYY-MM-DD HH:mm:ss');
          }
          return {
            [key]: processedValue,
          }
        }).reduce((a, obj) => ({ ...a, ...obj }), {});

        setMetaData({
          entity: entityNames?.[entity || entityId || localEntity],
          ranch: ranchNames?.[ranch || ranchId || localRanch],
          block: blockNames?.[block || blockId || localBlock],
          ...processedResponse
        });
      } else {
        notification.error({
          message: t('something went wrong'),
          description: response?.message,
          maxCount: 1
        })
      }
      setMetaDataLoading(false);
    })
  }

  function handleRebootModelCancel() {
    setShowRebootModal(false);
  }

  function handleRebootModelOk() {
    const rowKey = selectedNode?.key;
    if(rowKey) {
      setLoadingMap((prev) => ({
        ...prev,
        [rowKey]: { ...prev[rowKey], reboot: true }
      }));
    }

    const { identifier, sub_type } = selectedNode || {};
    if (identifier) {
      rebootNodeByIdentifier(identifier, sub_type, (result, status) => {
        if (status) {
          notification.success({
            message: result?.message || `Successfully rebooted (Identifier: ${identifier}`,
            maxCount: 1
          })
        } else {
          notification.success({
            message: result?.message || `Failed to reboot (Identifier: ${identifier}`,
            maxCount: 1
          })
        }
        if (rowKey) {
          setLoadingMap((prev) => ({
            ...prev,
            [rowKey]: { ...prev[rowKey], reboot: false }
          }));
        }
        setShowRebootModal(false);
      });
    }
  }

  return (
    <div className="single-column-page padded desktop-container desktop-container-config"
     style={!isMobile ? { maxWidth } : {}}
    >
      {(!showControl && (!showGraph && !showLoraGraph)) && (
        <>
          {!isMobile && <div className="flex-space-between align-baseline">
            <div className="custom-page-header">
              <Typography.Title level={3}>{title}</Typography.Title>
            </div>
            {localEntity && configList?.length > 0 && <Button
              key={1}
              onClick={() => {
                console.log('refresh', form?.getFieldsValue())
                onFinish(form?.getFieldsValue(), true)
              }}
              loading={refreshLoading}
              disabled={loading}
              icon={<ReloadOutlined />}
            >
              <span className="header-button-text">{t('Refresh')}</span>
            </Button>}
          </div>}
          <div>
            {lastUpdated && (
              <div className="last-update display-block">
                <span className="font-bold">{`${t('Last Updated')}: `}</span>
                <span>{lastUpdated}</span>
              </div>
            )}
          </div>
          <div className="flex-space-between align-baseline node-operations-filter-row">
            {isMobile && <div className="">
              <Typography.Title level={3}>{title}</Typography.Title>
            </div>}
            {!isMobile && renderFilterForm()}
            {renderSearch()}
          </div>
          {isMobile && renderFilterForm()}
          <EditableTable
            data={sortedConfigData}
            columns={getDynamicColumn(selectedSensorSubtype)}
            showIsActive={false}
            showAction={false}
            scroll={{ x: getDynamicColumn(selectedSensorSubtype)?.length * 180}}
            loading={loading || refreshLoading || aSLoading}
            onRow={nonLoraNodeSubtypes.includes(selectedSensorSubtype) ? (record) => ({
              onClick: () => onRowClick(record),
              style: { cursor: 'pointer' },
            }) : undefined}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "" : "table-row-dark"
            }
            handlePageChange={(page, pageSize) => handlePageChange(page, pageSize, sortedConfigData)}
            isDataSorted
          />
        </>
      )}
      {renderNodeHealthInfo()}
      {renderNodeHealthGraph()}
      {renderLoraNodeGraph()}
      <ReusableModal
        open={showModal}
        title={<div className="custom-page-header">{t('Node Meta Data')}</div>}
        onCancel={() => {
          setShowModal(false);
          setMetaData(undefined);
        }}
        centered
        width={modalWidth}
        footer={undefined}
      >
        <div className="meta-data-list">
          {metaData && Object.entries(metaData || {}).map(([key, value]: any) => (
            <div key={key} className="flex-space-between border-bottom">
              <span className="font-bold text-capital">{`${nodeMetaDataLabels[key]
                || key?.replace(/_/g, " ")} : `}</span>
              <span>{value || '-'}</span>
            </div>
          ))}
          {!metaDataLoading && !metaData && (
            <div className="no-data-card text-center">
              <Typography.Text>{t('No Data')}</Typography.Text>
            </div>
          )}
          {metaDataLoading && <LoadingOutlined className="loader" />}
        </div>
      </ReusableModal>
      <ReusableModal
        key={`Modal-key-${selectedNode?.identifier}`}
        open={showRebootModal}
        title={<div className="custom-page-header">{t('Node Reboot')}</div>}
        onCancel={handleRebootModelCancel}
        centered
        width={modalWidth}
        footer={[
          <Button type="default" onClick={handleRebootModelCancel} disabled={loadingMap?.[selectedNode?.key]?.reboot || false}>{t('Cancel')}</Button>,
          <Button type="primary" onClick={handleRebootModelOk} loading={loadingMap?.[selectedNode?.key]?.reboot || false}>{t('Reboot')}</Button>]}
      >
        <div className="text-center">
          <Typography.Text>
            {`Are you sure you want to reboot node with identifier `}
            <span className='bold-text'>{selectedNode?.identifier}</span>
            </Typography.Text>
        </div>
      </ReusableModal>
    </div>
  );
};

export default NodeOperations;
